import { Absence, OccasionalSlot, RegularCancellationRequest, RegularSlot, RequestSlot } from '@/types/Slots.ts'
import { Work } from '@/types/work.ts'
import moment, { Moment } from 'moment/moment'
import { CalendarEventType, EventCalendar } from '@/types/Calendar.ts'
import { RateSlot, RateType } from '@/types/StaffRate.ts'

// REGULAR
export function getKPIRegularRequest(calendarEvents: EventCalendar[], startDate: Moment, endDate: Moment): number {
    return calendarEvents
        .filter((event: EventCalendar): boolean => event.type === CalendarEventType.REGULAR_REQUEST)
        .filter((event: EventCalendar) =>
            moment(moment(event.start).format('DD-MM-YYYY'), 'DD-MM-YYYY').isBetween(startDate, endDate, 'days', '[]')
        )
        .flatMap((event: EventCalendar) => event.slots as RegularCancellationRequest[]).length
}

export function getKPIAbsence(calendarEvents: EventCalendar[], startDate: Moment, endDate: Moment): number {
    return calendarEvents
        .filter((event: EventCalendar): boolean => event.type === CalendarEventType.ABSENCE)
        .filter((event: EventCalendar) =>
            moment(moment(event.start).format('DD-MM-YYYY'), 'DD-MM-YYYY').isBetween(startDate, endDate, 'days', '[]')
        )
        .flatMap((event: EventCalendar) => event.slots as Absence[]).length
}

export function getKPIRegularSlot(calendarEvents: EventCalendar[], startDate: Moment, endDate: Moment): number {
    return calendarEvents
        .filter((event: EventCalendar): boolean => event.type === CalendarEventType.REGULAR)
        .filter((event: EventCalendar) =>
            moment(moment(event.start).format('DD-MM-YYYY'), 'DD-MM-YYYY').isBetween(startDate, endDate, 'days', '[]')
        )
        .flatMap((event: EventCalendar) => event.slots as RegularSlot[])
        .map((slot: RegularSlot) => slot.id)
        .filter((value: number, index: number, self: number[]): boolean => self.indexOf(value) === index).length
}

// OCCASIONAL
export function getKPIRequestedHours(calendarEvents: EventCalendar[]): number {
    return calendarEvents
        .filter((event: EventCalendar) => event.type === CalendarEventType.REQUEST)
        .flatMap((event: EventCalendar) => event.slots as RequestSlot[]).length
}

export function getKPIScheduledHours(calendarEvents: EventCalendar[], merger: boolean): number {
    const filteredEvents: OccasionalSlot[] = calendarEvents
        .filter((event: EventCalendar): boolean => event.type === CalendarEventType.SCHEDULED)
        .flatMap((event: EventCalendar) => event.slots as OccasionalSlot[])

    if (!merger) return filteredEvents.length

    const minutes: number = filteredEvents
        .map((slot: OccasionalSlot) => {
            const start: Moment = moment(slot.start, 'HH:mm:ss')
            const end: Moment = moment(slot.end, 'HH:mm:ss')
            return end.diff(start, 'minutes')
        })
        .reduce((acc: number, val: number) => acc + val, 0)

    return convertMinutesToRoundedHours(minutes)
}

export function getKPIAvailableHours(calendarEvents: EventCalendar[], merger: boolean): number {
    const filteredEvents: OccasionalSlot[] = calendarEvents
        .filter(
            (event: EventCalendar): boolean =>
                event.type === CalendarEventType.AVAILABLE ||
                event.type === CalendarEventType.REMAINDER_AVAILABLE ||
                event.type === CalendarEventType.EXPIRED
        )
        .flatMap((event: EventCalendar) => event.slots as OccasionalSlot[])

    if (!merger) return filteredEvents.length

    const minutes: number = filteredEvents
        .map((slot: OccasionalSlot) => {
            const start: Moment = moment(slot.start, 'HH:mm:ss')
            const end: Moment = moment(slot.end, 'HH:mm:ss')
            return end.diff(start, 'minutes')
        })
        .reduce((acc: number, val: number) => acc + val, 0)

    return convertMinutesToRoundedHours(minutes)
}

export function getKPIValidatedHours(calendarEvents: EventCalendar[], merger: boolean): number {
    const filteredEvents: OccasionalSlot[] = calendarEvents
        .filter((event: EventCalendar): boolean => event.type === CalendarEventType.VALIDATED)
        .flatMap((event: EventCalendar) => event.slots as OccasionalSlot[])

    if (!merger) return filteredEvents.length

    const minutes: number = filteredEvents
        .map((slot: OccasionalSlot) => {
            const start: Moment = moment(slot.start, 'HH:mm:ss')
            const end: Moment = moment(slot.end, 'HH:mm:ss')
            return end.diff(start, 'minutes')
        })
        .reduce((acc: number, val: number) => acc + val, 0)

    return convertMinutesToRoundedHours(minutes)
}

// STAFF
export function getKPIStaffHoursInSection(calendarEvents: EventCalendar[]): number {
    const minutes: number = calendarEvents
        .filter((event: EventCalendar): boolean => event.type === CalendarEventType.IN_SECTION)
        .flatMap((event: EventCalendar): Work => event.slots as Work)
        .map((slot: Work) => {
            const start: Moment = moment(slot.start, 'HH:mm:ss')
            const end: Moment = moment(slot.end, 'HH:mm:ss')
            return end.diff(start, 'minutes')
        })
        .reduce((acc: number, val: number) => acc + val, 0)

    return convertMinutesToRoundedHours(minutes)
}

export function getKPIStaffHoursNotInSection(calendarEvents: EventCalendar[]): number {
    const minutes: number = calendarEvents
        .filter((event: EventCalendar): boolean => event.type === CalendarEventType.NOT_IN_SECTION)
        .flatMap((event: EventCalendar): Work => event.slots as Work)
        .map((slot: Work): number => {
            const start: Moment = moment(slot.start, 'HH:mm:ss')
            const end: Moment = moment(slot.end, 'HH:mm:ss')
            return end.diff(start, 'minutes')
        })
        .reduce((acc: number, val: number): number => acc + val, 0)

    return convertMinutesToRoundedHours(minutes)
}

export function getKPIStaffHoursHoliday(calendarEvents: EventCalendar[]): number {
    const minutes: number = calendarEvents
        .filter((event: EventCalendar): boolean => event.type === CalendarEventType.STAFF_HOLIDAY)
        .flatMap((event: EventCalendar): Work => event.slots as Work)
        .map((slot: Work): number => {
            const start: Moment = moment(slot.start, 'HH:mm:ss')
            const end: Moment = moment(slot.end, 'HH:mm:ss')
            return end.diff(start, 'minutes')
        })
        .reduce((acc: number, val: number): number => acc + val, 0)

    return convertMinutesToRoundedHours(minutes)
}

export function getKPIStaffHoursTraining(calendarEvents: EventCalendar[]): number {
    const minutes: number = calendarEvents
        .filter((event: EventCalendar): boolean => event.type === CalendarEventType.STAFF_TRAINING)
        .flatMap((event: EventCalendar): Work => event.slots as Work)
        .map((slot: Work): number => {
            const start: Moment = moment(slot.start, 'HH:mm:ss')
            const end: Moment = moment(slot.end, 'HH:mm:ss')
            return end.diff(start, 'minutes')
        })
        .reduce((acc: number, val: number): number => acc + val, 0)

    return convertMinutesToRoundedHours(minutes)
}

function convertMinutesToRoundedHours(minutes: number): number {
    if (minutes === 0) return 0

    const hours: number = minutes / 60
    return Math.round(hours) || 1
}

// Rate
export function getSuccessRate(calendarEvents: EventCalendar[]): number {
    return getRateByType(calendarEvents, RateType.success)
}

export function getWarningRate(calendarEvents: EventCalendar[]): number {
    return getRateByType(calendarEvents, RateType.warning)
}

export function getErrorRate(calendarEvents: EventCalendar[]): number {
    return getRateByType(calendarEvents, RateType.error)
}

function getRateByType(calendarEvents: EventCalendar[], type: RateType): number {
    const minutes: number = calendarEvents
        .filter((event: EventCalendar): boolean => event.type === CalendarEventType.RATE_SLOT)
        .flatMap((event: EventCalendar) => event.slots as RateSlot[])
        .filter((slot: RateSlot): boolean => slot.type === type)
        .map((slot: RateSlot) => {
            const start: Moment = moment(slot.start, 'HH:mm:ss')
            const end: Moment = moment(slot.end, 'HH:mm:ss')
            return end.diff(start, 'minutes')
        })
        .reduce((acc: number, val: number) => acc + val, 0)

    return convertMinutesToRoundedHours(minutes)
}
